import type { Lesson } from './types'

/**
 * The four rules of Noon Sakinah (ن with sukun) and Tanween - the first and
 * largest set of rules in Tuhfatul Atfal. Every one of these 28 letters is
 * accounted for across the four rules; together they explain what happens
 * to the "n" sound depending on which letter follows it.
 */
export const noonRulesLessons: Lesson[] = [
  {
    id: 'noon-izhar',
    title: 'Izhar (Clear Pronunciation)',
    summary: 'Pronounce the "n" sound clearly and fully when followed by a throat letter.',
    explanation: [
      'Izhar (إظهار, "to make clear") applies when noon sakinah or tanween is followed by one of the six throat letters: ء ه ع ح غ خ.',
      'The "n" sound is pronounced clearly and fully, with no nasalization or blending into the next letter - because these letters come from deep in the throat, far from where noon is articulated, there is no risk of the sounds running together, so nothing needs to be hidden or merged.',
    ],
    colorLegend: [{ color: 'izhar', label: 'Noon/tanween pronounced clearly' }],
    examples: [
      { arabic: 'مِنْ آمَنَ', transliteration: 'man āmana', meaning: 'whoever believes', highlights: [{ text: 'مِنْ', color: 'izhar' }] },
      { arabic: 'مَنْ هَاجَرَ', transliteration: 'man hājara', meaning: 'whoever emigrated', highlights: [{ text: 'مَنْ', color: 'izhar' }] },
      { arabic: 'عَلِيمٌ حَكِيمٌ', transliteration: 'ʿalīmun ḥakīm', meaning: 'All-Knowing, All-Wise', highlights: [{ text: 'مٌ', color: 'izhar' }] },
      { arabic: 'مِنْ خَيْرٍ', transliteration: 'min khayr', meaning: 'of any good', highlights: [{ text: 'مِنْ', color: 'izhar' }] },
    ],
  },
  {
    id: 'noon-idgham',
    title: 'Idgham (Merging)',
    summary: 'The "n" sound disappears entirely into the next letter, doubling it instead.',
    explanation: [
      'Idgham (إدغام, "to merge") applies when noon sakinah or tanween is followed by one of six letters, remembered by the mnemonic word يَرْمَلُونَ (yarmalūn): ي ر م ل و ن.',
      'Instead of pronouncing the "n" and then the next letter separately, the two blend into one doubled letter - the noon sound vanishes and the following letter is pronounced with a shaddah (emphasis).',
      'This splits into two types depending on the letter:',
      'Idgham with ghunnah (nasalization) - before ي ن م و (mnemonic يَنْمُو, "yanmu") - the merge keeps a nasal hum for about two counts.',
      'Idgham without ghunnah - before ر or ل only - the merge happens with no nasal hum at all.',
      'Note: idgham only applies between two separate words, or between a word ending in tanween and the next word. Within a single word, noon sakinah followed by و or ي is read with izhar instead (e.g. دُنْيَا, بُنْيَان) - this exception exists precisely so words are not distorted.',
    ],
    colorLegend: [
      { color: 'idghamGhunnah', label: 'Idgham with ghunnah (before ي ن م و)' },
      { color: 'idgham', label: 'Idgham without ghunnah (before ر ل)' },
    ],
    examples: [
      { arabic: 'مَنْ يَقُولُ', transliteration: 'may yaqūl', meaning: 'whoever says', highlights: [{ text: 'مَنْ', color: 'idghamGhunnah' }] },
      { arabic: 'مِنْ نِعْمَةٍ', transliteration: 'min niʿmah', meaning: 'of a favor', highlights: [{ text: 'مِنْ', color: 'idghamGhunnah' }] },
      { arabic: 'هُدًى لِلْمُتَّقِينَ', transliteration: 'hudal lil-muttaqīn', meaning: 'guidance for the righteous', highlights: [{ text: 'دًى', color: 'idgham' }] },
      { arabic: 'مِنْ رَبِّهِمْ', transliteration: 'mir rabbihim', meaning: 'from their Lord', highlights: [{ text: 'مِنْ', color: 'idgham' }] },
    ],
  },
  {
    id: 'noon-iqlab',
    title: 'Iqlab (Conversion)',
    summary: 'Before the letter ب, the "n" sound is converted into a hidden "m" sound.',
    explanation: [
      'Iqlab (إقلاب, "to convert") has only one trigger letter: ب. Whenever noon sakinah or tanween is followed by baa, the "n" sound is converted into an "m" sound - held with a light nasal hum for about two counts - and a small meem is often written above the noon in a Mushaf as a visual reminder.',
      'This is the only one of the four rules with just a single letter, which makes it the easiest to spot once you know to watch for ب.',
    ],
    colorLegend: [{ color: 'iqlab', label: 'Noon/tanween converted to a hidden "m" sound' }],
    examples: [
      { arabic: 'مِنْ بَعْدِ', transliteration: 'mim baʿdi', meaning: 'after', highlights: [{ text: 'مِنْ', color: 'iqlab' }] },
      { arabic: 'سَمِيعٌ بَصِيرٌ', transliteration: 'samīʿam baṣīr', meaning: 'All-Hearing, All-Seeing', highlights: [{ text: 'عٌ', color: 'iqlab' }] },
      { arabic: 'أَنْبِئْهُمْ', transliteration: 'ambiʾhum', meaning: 'inform them', highlights: [{ text: 'نْ', color: 'iqlab' }] },
    ],
  },
  {
    id: 'noon-ikhfa',
    title: 'Ikhfa (Concealment)',
    summary: 'The "n" sound is neither fully pronounced nor fully merged - it is concealed, with a light nasal hum.',
    explanation: [
      'Ikhfa (إخفاء, "to conceal") applies to the remaining fifteen letters not covered by the other three rules: ت ث ج د ذ ز س ش ص ض ط ظ ف ق ك.',
      'The "n" sound is hidden - not pronounced clearly like izhar, and not merged away like idgham - but held partway, with the tongue positioned as if about to say noon, while a nasal hum (ghunnah) is sustained for about two counts before moving into the next letter.',
      'Ikhfa is the largest of the four rules by letter count, so it is worth memorizing the other three groups (izhar\'s 6, idgham\'s 6, iqlab\'s 1) and treating everything else as ikhfa by elimination.',
    ],
    colorLegend: [{ color: 'ikhfa', label: 'Noon/tanween concealed with a light nasal hum' }],
    examples: [
      { arabic: 'مِنْ قَبْلُ', transliteration: 'min qablu', meaning: 'before', highlights: [{ text: 'مِنْ', color: 'ikhfa' }] },
      { arabic: 'أَنْتُمْ', transliteration: 'antum', meaning: 'you (pl.)', highlights: [{ text: 'نْ', color: 'ikhfa' }] },
      { arabic: 'رِيحٍ صَرْصَرٍ', transliteration: 'rīḥin ṣarṣar', meaning: 'a furious wind', highlights: [{ text: 'حٍ', color: 'ikhfa' }] },
      { arabic: 'جَنَّاتٍ تَجْرِي', transliteration: 'jannātin tajrī', meaning: 'gardens beneath which [rivers] flow', highlights: [{ text: 'تٍ', color: 'ikhfa' }] },
    ],
  },
]
